import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { AppLayout } from '../../components/templates/AppLayout/AppLayout';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/Sidebar';
import { Tabs } from '../../components/atoms/Tabs/Tabs';
import { PageContent } from '../../components/templates/PageContent/PageContent';
import { PageHeader } from '../../components/templates/PageHeader/PageHeader';
import { FaunaMarinPanel } from '../../components/integrations/FaunaMarinPanel/FaunaMarinPanel';
import { FocustronicPanel } from '../../components/integrations/FocustronicPanel/FocustronicPanel';
import { ReefBotPanel } from '../../components/integrations/ReefBotPanel/ReefBotPanel';
import { ControllerPanel } from '../../components/controller/ControllerPanel';
import { CameraSourcesPanel } from '../../components/cameraSources/CameraSourcesPanel';

/**
 * Tab ids are the last path segment (/aquariums/:aquariumId/integrations/<tab>).
 * Fauna Marin is last while its interface is not enabled yet.
 */
const TABS = ['controller', 'camera', 'focustronic', 'reefbot', 'fauna-marin'] as const;
type TabId = (typeof TABS)[number];

export function IntegrationsPage() {
  const { t } = useTranslation();
  const { aquariumId } = useParams<{ aquariumId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const segment = location.pathname.split('/').filter(Boolean).pop() ?? '';
  const activeId: TabId = (TABS as readonly string[]).includes(segment) ? (segment as TabId) : 'controller';

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width={activeId === 'fauna-marin' || activeId === 'focustronic' ? 'narrow' : 'default'}>
          <PageHeader title={t('integrations.title')} description={t('integrations.subtitle')} />
          <Tabs
            aria-label={t('integrations.title')}
            items={[
              // Each panel is rendered only while active, so the other tabs' queries do not run.
              { id: 'controller', label: t('controller.tab'), panel: activeId === 'controller' ? <ControllerPanel aquariumId={aquariumId} /> : null },
              { id: 'camera', label: t('cameraSources.tab'), panel: activeId === 'camera' ? <CameraSourcesPanel aquariumId={aquariumId} /> : null },
              { id: 'focustronic', label: 'Focustronic', panel: activeId === 'focustronic' ? <FocustronicPanel aquariumId={aquariumId} /> : null },
              { id: 'reefbot', label: 'ReefBot', panel: activeId === 'reefbot' ? <ReefBotPanel aquariumId={aquariumId} /> : null },
              { id: 'fauna-marin', label: 'Fauna Marin', panel: activeId === 'fauna-marin' ? <FaunaMarinPanel aquariumId={aquariumId} /> : null },
            ]}
            activeId={activeId}
            onChange={(id) => navigate(`/aquariums/${aquariumId ?? ''}/integrations/${id}`)}
          />
        </PageContent>
      }
    />
  );
}

export default IntegrationsPage;
